<template>
  <div>
    <div :class="$style.wrapper">
      <h2 :class="$style.title">
        {{ title }}
      </h2>
      <input
        v-if="isCountRow"
        v-model.number="countRow"
        type="number"
        :class="$style.input"
        :max="data.length"
        :min="1"
      >
    </div>
    <table :class="$style.table">
      <thead v-if="columns">
        <tr :class="$style.header">
          <th
            v-for="column in columns"
            :key="column.name"
            :style="{
              width: `${column.width}%`,
            }"
            @click="onColumnClick(column)"
          >
            {{ column.name }}
            <img
              v-if="sortedColumn === column.field"
              src="@/assets/images/icons/table/sort-arrow.svg"
              :class="[
                $style.tdInline,
                $style.arrow,
                {
                  [$style.asc]: !isSortAscending,
                },
              ]"
            >
          </th>
        </tr>
      </thead>
      <tbody v-if="keyReport">
        <tr
          v-for="[key, value] in Object.entries(data)"
          :key="key"
          :class="$style.item"
          @click="$emit('goTo', key)"
        >
          <td :class="[$style.user, $style.key]">
            <span :class="{ [$style.clickable]: isClickable(key) }">
              {{ $t(`reports.${keyReport}.${key}`) }}
            </span>
          </td>
          <td :class="[$style.user, $style.key]">
            <span :class="{ [$style.clickable]: isClickable(key) }">
              {{ value }}
            </span>
          </td>
        </tr>
      </tbody>
      <tbody v-else>
        <tr
          v-for="(row, idx) in tableData"
          :key="idx"
          :class="[$style.item, { [$style.highlight]: row.id === rowActiveId }, productivityClass(row.productivity)]"
        >
          <td
            v-for="column in columns"
            :key="column.field"
            :class="$style.user"
          >
            {{ getDataField(row, column.field) }}
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script>
  import { getDataField } from '../worktime/table-field';
  import { CLICKABLE_KEYS } from './const/const';

  export default {
    props: {
      columns: {
        type: Array,
        required: false,
        default: null,
      },
      data: {
        type: [Array, Object],
        required: true,
      },
      keyReport: {
        type: String,
        required: false,
        default: '',
      },
      title: {
        type: String,
      },
      clickable: {
        type: Boolean,
        default: false,
      },
      isCountRow: {
        type: Boolean,
        default: false,
      },
    },
    data: () => ({
      sortedColumn: null,
      rowActiveId: null,
      isSortAscending: true,
      countRow: 10,
    }),
    computed: {
      tableData() {
        const { sortedColumn, isSortAscending } = this;

        const sortedColumnObject = this.columns.find((i) => i.field === sortedColumn);

        if (sortedColumnObject && typeof sortedColumnObject.sortFn === 'function') {
          const data = [...this.data].sort(sortedColumnObject.sortFn(isSortAscending));
          return this.isCountRow ? data.slice(0, this.countRow) : data;
        }

        const data = [...this.data].sort(this.compare);

        return this.isCountRow ? data.slice(0, this.countRow) : data;
      },
    },
    created() {
      const currentSorted = this.columns?.find((item) => item?.sorted);
      if (currentSorted) this.sortedColumn = currentSorted?.field;
    },
    methods: {
      isClickable(key) {
        return this.clickable && CLICKABLE_KEYS.includes(key);
      },
      onRowClick(key) {
        if (this.isClickable(key)) {
          this.$emit('goTo', key);
        }
      },
      compare(a, b) {
        a = a[this.sortedColumn];
        b = b[this.sortedColumn];

        if (typeof a === 'string' && typeof b === 'string') {
          a = a.toLowerCase();
          b = b.toLowerCase();
        }

        if (this.isSortAscending) {
          if (a > b) return -1;
          if (a < b) return 1;
          return 0;
        }

        if (a < b) return -1;
        if (a > b) return 1;

        return 0;
      },
      onColumnClick(column) {
        if (!column?.sorted) return;
        if (column.field === this.sortedColumn) {
          this.isSortAscending = !this.isSortAscending;
        } else {
          this.sortedColumn = column.field;
          this.isSortAscending = true;
        }
      },
      getDataField(data, field) {
        return getDataField(data[field], field);
      },
      productivityClass(productivity) {
        switch (productivity) {
          case 0:
            return this.$style.red;
          case 1:
            return this.$style.green;
          case 2:
            return this.$style.yellow;
          default:
            return '';
        }
      },
    },
  };
</script>

<style lang="scss" module>
  .wrapper {
    display: flex;
    align-items: center;
  }
  .title {
    padding: 10px 2px 10px 10px;
  }
  .input {
    border: 1px solid rgba(60, 60, 60, 0.26);
    border-radius: 4px;
    height: 24px;
    width: 40px;
  }
  .table {
    width: 100%;
    margin-bottom: 15px;
    border-collapse: collapse;
    font-weight: 400;
    word-break: break-word;
    border: 1px solid $borderColor;
    padding-bottom: 20px;
  }
  .header {
    background: #f5f5f5;
    height: 38px;
    position: sticky;
    z-index: 3;
    th {
      border-right: 1px solid $borderColor;
      border-left: 1px solid $borderColor;
      align-items: center;
      text-align: center;
      color: #232326;
      letter-spacing: -0.08px;
      font-size: 13px;
      padding: 10px 4px 10px 10px;
      cursor: pointer;
    }
  }

  .item {
    height: 29px;
    border-bottom: 0.2px solid #eee;

    &:hover {
      background: $light-gray;
    }
    &.red {
      background-color: #ffb0b0;
      &:hover {
        background-color: $red;
      }
    }

    &.green {
      background-color: #7cd890;
      &:hover {
        background-color: #1f8d37;
      }
    }

    &.yellow {
      background-color: #ffecbc;
      &:hover {
        background-color: #d49a02;
      }
    }

    td {
      border-right: 1px solid $borderColor;
      border-left: 1px solid $borderColor;
      align-items: center;
      letter-spacing: -0.24px;
      color: $black;
      font-weight: 300;
      font-size: 12px;
      line-height: 14px;
      text-align: center;
      padding: 0px 5px;

      &.user {
        font-size: 14px;
        letter-spacing: 0px;
        text-align: left;
        padding-top: 7px;
        padding-right: 4px;
        padding-bottom: 7px;
      }
      &.key {
        width: 50%;
      }
    }
  }

  .highlight {
    animation: flash 10s ease;
  }

  @keyframes flash {
    0% {
      background: $dark-gray;
    }
    100% {
      background: transparent;
    }
  }

  .clickable {
    cursor: pointer;
    &:hover {
      text-decoration: underline;
    }
    &.active {
      text-decoration: underline;
    }
  }

  .tdInline {
    display: inline-flex;
  }

  .arrow {
    margin-left: 4px;

    &.asc {
      transform: rotate(180deg);
    }

    &.hidden {
      visibility: hidden;
    }
  }
</style>
